import { pick, startCase } from 'lodash/fp';
import { FC, Fragment, useEffect, useState } from 'react';
import { Controller } from 'react-hook-form';
import { useSelector } from 'react-redux';

import Divider from 'dashboard/components/Divider';
import FormError from 'dashboard/components/FormControls/FormError';
import ModalWrapper from 'dashboard/components/ModalWrapper';
import ConsoleOutput from 'dashboard/components/WorkflowControlPanel/StepConfigAction/ConsoleOutput';
import CodeHighlight from 'dashboard/components-lib/CodeHighlight';
import { ModalBody, ModalContainer, ModalHeader } from 'dashboard/layouts/ModalLayout';
import SessionClientDashboard from 'dashboard/lib/clients/SessionClientDashboard';
import { Workflow, WorkflowFieldObjectKey } from 'dashboard/models/workflow';
import { LogSeverity } from 'dashboard/models/workflow-step';
import { RootState } from 'dashboard/redux/store';
import Button from 'design-system/components/Button';
import Input from 'design-system/components/Input';
import Select, { Option } from 'design-system/components/Select';
import Spacer from 'design-system/components/Spacer';
import { Label, Paragraph } from 'design-system/components/Text';
import TextArea from 'design-system/components/TextArea';
import { LocalStorageKey } from 'lib/clients/StorageClient';
import { FieldSchemaJson } from 'lib/fields';
import useApiCallback from 'lib/hooks/useApiCallback';
import useLocalStorage from 'lib/hooks/useLocalStorage';
import { ApiModel } from 'lib/interfaces/api';
import { useFormSecurely } from 'lib/security/reactHookForm';

type ApiResponse = {
  result: boolean;
  payload: string;
  logs: string;
};
type Response = {
  result: boolean;
  payload: string;
  logs: [LogSeverity, unknown[]][];
};

type Form = {
  argNameToValue: Record<string, string>;
};

const parseJSONsafely = <T extends boolean | string | number>(json: string, fallback: T) => {
  let parsed = fallback;
  try {
    parsed = JSON.parse(json) as T;
  } catch {
    // do nothing
  }
  return parsed;
};

interface RunThisCodeModalProps {
  args: { name: string; type: string }[];
  returnSchema: FieldSchemaJson;
  serializedCode: string;
  nitro: boolean;
  onClose: () => void;
}

const RunThisCodeModal: FC<RunThisCodeModalProps> = ({
  args,
  returnSchema,
  serializedCode,
  nitro,
  onClose,
}) => {
  const { environmentId, organizationId } = useSelector<
    RootState,
    { environmentId: string; organizationId: string }
  >((state) => ({
    environmentId: state.currentEnvironment.currentEnvironment.id,
    organizationId: state.organization.organization.id,
  }));
  const [cachedFormData, saveFormData] = useLocalStorage<{
    organizationId: string;
    inputArgs: Record<string, string>;
  }>(LocalStorageKey.DashboardWorkflowEditorRunCodeFormInputs, {
    organizationId: '',
    inputArgs: {},
  });

  const { control, handleSubmit } = useFormSecurely<Form>({
    defaultValues: {
      argNameToValue: args.reduce<Record<string, string>>(
        (acc, arg) => {
          return acc[arg.name]
            ? acc
            : {
                ...acc,
                [arg.name]: ['object', 'unknown'].includes(arg.type)
                  ? '{}'
                  : // booleans default to "false" in the input, so we also update form state value for this arg to false.
                    ['boolean'].includes(arg.type)
                    ? 'false'
                    : '',
              };
        },
        cachedFormData.organizationId === organizationId
          ? pick(
              args.map(({ name }) => name),
              cachedFormData.inputArgs,
            )
          : {},
      ),
    },
  });

  const [runResponse, setResponse] = useState<Response | null>(null);

  const { callback: evaluateCode, loading } = useApiCallback(
    async (argNameToValue: Record<string, string> = {}) => {
      const inputArgs: { name: string; value: string; type: string }[] = args.map((arg) => ({
        ...arg,
        value: argNameToValue[arg.name] ?? 'null',
      }));

      return SessionClientDashboard.postResourceSingletonAction<
        Workflow,
        { type: ApiModel.Workflow },
        ApiResponse
      >({ type: ApiModel.Workflow }, 'try-evaluate-code', {
        meta: {
          'environment-id': environmentId,
          'input-args': inputArgs,
          'return-schema': returnSchema,
          'serialized-code': serializedCode,
          nitro,
        },
      }).then(({ meta }) => {
        saveFormData({
          organizationId,
          inputArgs: { ...cachedFormData.inputArgs, ...argNameToValue },
        });
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        setResponse({
          ...meta,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          payload: meta.payload != null ? JSON.parse(meta.payload) : meta.payload,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          logs: meta.logs != null ? JSON.parse(meta.logs) : meta.logs,
        });
      });
    },
    [environmentId, serializedCode, setResponse, nitro],
  );

  // Submit automatically if there is no passed arguments
  useEffect(() => {
    if (args.length === 0) {
      evaluateCode();
    }
  }, [args.length, evaluateCode]);

  return (
    <ModalWrapper isOpen handleCloseModal={onClose}>
      <ModalContainer id='run-this-code-modal__container'>
        <ModalHeader onClose={onClose}>Run Code</ModalHeader>
        <ModalBody>
          <form onSubmit={handleSubmit(({ argNameToValue }) => evaluateCode(argNameToValue))}>
            {args.length > 0 && (
              <>
                <Label size='lg' weight='bold'>
                  Mock Arguments
                </Label>
                {args.map((arg, i) => (
                  <Fragment key={i}>
                    <Spacer height={1} />
                    <Label htmlFor={`run-this-code__mock-${arg.name}`} size='md' weight='bold'>
                      {arg.name}
                    </Label>
                    <Spacer height={1} />
                    <Controller
                      control={control}
                      name={`argNameToValue.${arg.name}`}
                      rules={{
                        required: 'Required',
                        validate: {
                          jsonable: (v) => {
                            if (!['object', 'array', 'unknown'].includes(arg.type)) {
                              return true;
                            }
                            try {
                              return typeof JSON.parse(v) === 'object';
                            } catch {
                              return 'Invalid JSON';
                            }
                          },
                          token: (v) => {
                            if (
                              Object.values(WorkflowFieldObjectKey).find(
                                (objectType) => objectType === arg.type,
                              ) == null
                            ) {
                              return true;
                            }
                            const [_prefix, uid, ...others] = parseJSONsafely(v, '').split('_');
                            return others.length === 0 && [24, 28].includes(uid?.length)
                              ? true
                              : 'Invalid Token';
                          },
                          date: (v) => {
                            if (arg.type !== 'date') return true;
                            try {
                              const input = JSON.parse(v);
                              return (
                                new Date(input).toISOString().split('T')[0] === input ||
                                'Invalid Date'
                              );
                            } catch {
                              return 'Invalid Date format';
                            }
                          },
                        },
                      }}
                      render={({ field: { value, onChange }, fieldState: { error } }) => {
                        const props = {
                          hasError: !!error,
                          id: `run-this-code__mock-${arg.name}`,
                          onChange,
                        };

                        const input = (() => {
                          switch (arg.type) {
                            // JsonSchemaTypes: Switch to conditional step's value selection eventually
                            case 'integer':
                            case 'number':
                              return (
                                <Input
                                  {...props}
                                  // Not syncing value and input intentionally to store JSON into value
                                  defaultValue={parseJSONsafely(value, 0)}
                                  type={arg.type === 'integer' ? 'number' : 'text'}
                                  onChange={(e) => onChange(JSON.stringify(Number(e.target.value)))}
                                />
                              );
                            case 'boolean':
                              return (
                                <Select
                                  {...props}
                                  defaultValue={String(parseJSONsafely(value, false))}
                                >
                                  <Option value='false'>False</Option>
                                  <Option value='true'>True</Option>
                                </Select>
                              );
                            case 'date':
                              return (
                                <Input
                                  {...props}
                                  type='date'
                                  defaultValue={parseJSONsafely(value, '')}
                                  onChange={(e) => {
                                    if (e.target.value !== '') {
                                      onChange(JSON.stringify(e.target.value));
                                    }
                                  }}
                                />
                              );
                            case 'object':
                            case 'array':
                            case 'unknown':
                              return <TextArea {...props} value={value} />;
                            case 'string':
                            default: // WorkflowObject
                              return (
                                <Input
                                  {...props}
                                  // Not syncing value and input intentionally to store JSON into value
                                  defaultValue={parseJSONsafely(value, '')}
                                  onChange={(e) => onChange(JSON.stringify(e.target.value))}
                                  placeholder={
                                    arg.type !== 'string' ? `ID for ${startCase(arg.type)}` : ''
                                  }
                                />
                              );
                          }
                        })();

                        return (
                          <>
                            {input}
                            {!!error && <FormError>{error.message}</FormError>}
                          </>
                        );
                      }}
                    />
                  </Fragment>
                ))}
              </>
            )}
            {runResponse && (
              <>
                <Spacer height={2} />
                {args.length > 0 && <Divider />}
                <Label size='lg' weight='bold'>
                  Returned Value
                </Label>
                {runResponse.result ? (
                  <>
                    <Spacer height={1} />
                    <CodeHighlight language='json'>
                      {JSON.stringify(runResponse.payload)}
                    </CodeHighlight>
                  </>
                ) : (
                  <Paragraph size='md'>Error: {runResponse.payload}</Paragraph>
                )}
                <Spacer height={2} />
                <Label size='lg' weight='bold'>
                  Console Output
                </Label>
                {runResponse.logs.length > 0 ? (
                  <>
                    <Spacer height={1} />
                    <ConsoleOutput logs={runResponse.logs} />
                  </>
                ) : (
                  <Paragraph size='md'>
                    No log by <code>console.log()</code>
                  </Paragraph>
                )}
              </>
            )}
            <Spacer height={4} />
            <Button type='submit' isPending={loading}>
              {runResponse ? 'Rerun' : 'Run'}
            </Button>
          </form>
        </ModalBody>
      </ModalContainer>
    </ModalWrapper>
  );
};

export default RunThisCodeModal;
